import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ThemedIconButton from "./ThemedIconButton";
import {
  DARK_THEME_TEXT_COLOR,
  LIGHT_THEME_TEXT_COLOR,
  DARK_THEME_INPUT_OUTLINE_COLOR,
  LIGHT_THEME_INPUT_OUTLINE_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
  LIGHT_THEME_DISABLED_TEXT_COLOR,
} from "../constants";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ProjectSearchBar = ({ 
  value,
  onSearchChange, 
  placeholder = "Search projects...", 
  isMobile = false,
  debounceDelay = 200 
}) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");

  const isControlled = value !== undefined;
  const displayValue = isControlled ? value : internalSearchTerm;

  // Debounce the search term (only used in uncontrolled mode)
  const debouncedSearchTerm = useDebounce(displayValue, debounceDelay);

  // Notify parent when debounced search term changes (uncontrolled mode only; controlled mode updates via handleSearchChange)
  useEffect(() => {
    if (!isControlled && onSearchChange) {
      onSearchChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearchChange, isControlled]);

  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    if (isControlled) {
      onSearchChange?.(newValue);
    } else {
      setInternalSearchTerm(newValue);
    }
  };

  const handleClearSearch = () => {
    if (isControlled) {
      onSearchChange?.("");
    } else {
      setInternalSearchTerm("");
    }
  };

  return (
    <Box
      sx={{
        p: isMobile ? 1.5 : 0,
        pb: isMobile ? 0.5 : 0,
        position: isMobile ? "sticky" : "static",
        top: isMobile ? 0 : "auto",
        zIndex: isMobile ? 1 : "auto",
        backgroundColor: isMobile 
          ? (darkThemeEnabled ? "#252525" : "#FAFAFA")
          : "transparent",
        mb: isMobile ? 0 : 0, // Remove bottom margin for desktop
        display: "flex",
        alignItems: "center",
      }}
    >
      <TextField
        size={isMobile ? "small" : "medium"}
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon 
                sx={{ 
                  color: darkThemeEnabled ? DARK_THEME_TEXT_COLOR : LIGHT_THEME_TEXT_COLOR 
                }} 
              />
            </InputAdornment>
          ),
          endAdornment: displayValue && (
            <InputAdornment position="end">
              <ThemedIconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ p: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </ThemedIconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: isMobile ? 40 : 36, // Match Material-UI button height
            color: darkThemeEnabled ? DARK_THEME_TEXT_COLOR : LIGHT_THEME_TEXT_COLOR,
            "& fieldset": {
              borderColor: darkThemeEnabled ? DARK_THEME_INPUT_OUTLINE_COLOR : LIGHT_THEME_INPUT_OUTLINE_COLOR,
            },
            "&:hover fieldset": {
              borderColor: darkThemeEnabled ? DARK_THEME_TEXT_COLOR : LIGHT_THEME_TEXT_COLOR,
            },
            "&.Mui-focused fieldset": {
              borderColor: darkThemeEnabled ? DARK_THEME_TEXT_COLOR : LIGHT_THEME_TEXT_COLOR,
            },
          },
          "& .MuiInputBase-input": {
            padding: isMobile ? "8px 14px" : "6px 14px", // Reduce vertical padding
            height: "auto", // Let height be controlled by container
          },
          "& .MuiInputBase-input::placeholder": {
            color: darkThemeEnabled ? DARK_THEME_DISABLED_TEXT_COLOR : LIGHT_THEME_DISABLED_TEXT_COLOR,
            opacity: 1,
          },
        }}
      />
    </Box>
  );
};

export default ProjectSearchBar;

